'use client'

import Link from 'next/link'
import clsx from 'clsx'
import DOMPurify from 'isomorphic-dompurify'
import Logo from '@/components/Shared/Logo'
import styles from './styles.module.scss'
import footerData from '@/constants/footer.json'
import talentbookMetaData from '@/constants/talentbookMetaData.json'
import { externalLinkMap } from '@/constants/externalLinkMap'

const resolvePath = (pathKey: string) => {
  return externalLinkMap[pathKey] || '#'
}

export default function Footer() {
  const { footerContentsTop, footerContentsBottom, footerBottom } = footerData

  return (
    <footer className={styles.footer}>
      <div className={styles.footerTop}>
        <div className={styles.footerTopInner}>
          <dl className={styles.contentsTop}>
            <dt className={styles.contentsTopLogo}>
              <Logo color='white' className={styles.footerLogo} />
            </dt>
            <dd className={styles.contentsTopInfo}>
              <div className={styles.contentsTopInfoInner}>
                <p className={styles.contentsTopDescription}>
                  {talentbookMetaData.siteDescription}
                </p>
                <Link href={footerContentsTop.buttonPath} className={styles.contentsTopLink}>
                  {footerContentsTop.buttonText}
                </Link>
              </div>
              <ul className={styles.snsList}>
                {footerContentsTop.sns.map((sns, index) => (
                  <li key={sns.pathKey || index} className={styles.snsItem}>
                    <Link
                      href={resolvePath(sns.pathKey)}
                      target='_blank'
                      rel='noopener noreferrer'
                      className={styles.snsLink}
                    >
                      <img
                        src={`/images/icon/sns/${sns.imageUrl}`}
                        width={24}
                        height={24}
                        alt={sns.pathKey}
                        className={styles.snsIcon}
                        loading='lazy'
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </dd>
          </dl>

          <div className={styles.contentsBottom}>
            {footerContentsBottom.linkGroup.map((group, index) => (
              <div key={index} className={styles.contentsBottomGroup}>
                <p className={styles.contentsBottomLinkTitle}>{group.title}</p>
                <ul className={styles.contentsBottomLinkList}>
                  {group.linkList.map((link, idx) => (
                    <li key={idx} className={styles.contentsBottomLinkItem}>
                      <Link
                        href={link.pathKey ? resolvePath(link.pathKey) : link.path || '/'}
                        target={link.pathKey ? '_blank' : undefined}
                        rel={link.pathKey ? 'noopener noreferrer' : undefined}
                        className={styles.contentsBottomLink}
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className={styles.bannerArea}>
              {footerContentsBottom.banner.map((banner, index) => (
                <Link
                  key={`${banner.pathKey}-${banner.type}-${index}`}
                  href={resolvePath(banner.pathKey)}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={clsx({
                    [styles.spnone]: banner.type === 'pc',
                    [styles.pcnone]: banner.type !== 'pc',
                  })}
                >
                  <img
                    src={`/images/footer/${banner.imageUrl}`}
                    width={300}
                    height={100}
                    alt={banner.alt}
                    className={styles.bannerImg}
                    loading='lazy'
                  />
                </Link>
              ))}

              <div className={styles.privacyIcon}>
                <a
                  href='https://privacymark.jp/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={styles.privacyLink}
                >
                  <img
                    src='/images/footer/pmark.png'
                    width={72}
                    height={72}
                    alt='pmark'
                    className={styles.pmarkLogo}
                    loading='lazy'
                  />
                </a>
                <img
                  src='/images/footer/isms.png'
                  width={154}
                  height={72}
                  alt='isms'
                  className={styles.ismsLogo}
                  loading='lazy'
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.footerBottom}>
        <div className={styles.footerBottomInner}>
          <ul className={styles.bottomLinkList}>
            {footerBottom.linkList.map((link, index) => (
              <li key={index} className={styles.bottomLinkItem}>
                <Link
                  href={link.pathKey ? resolvePath(link.pathKey) : link.path || '/'}
                  target={link.pathKey ? '_blank' : undefined}
                  rel={link.pathKey ? 'noopener noreferrer' : undefined}
                  className={styles.bottomLink}
                >
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
          <small
            className={styles.footerBottomCopy}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(footerBottom.copylight) }}
          />
        </div>
      </div>
    </footer>
  )
}
